import { HiLocationMarker, HiArrowUp } from "react-icons/hi";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

function SafeJsonParse(str, fallback = []) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

export default function Footer({ footer = [], contact = [] }) {
    const addresses = contact[2] ? SafeJsonParse(contact[2].value, []) : [];
    const socials = contact[1] ? SafeJsonParse(contact[1].value, []) : [];

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <footer className="relative mt-20 bg-[#0a0c12] border-t border-white/10 overflow-hidden">
            {/* Glowing Accent Top Line */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-pink via-ungu to-biruMuda" />

            <div className="py-12 md:py-16 px-6 sm:px-10 max-w-[1400px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
                    {/* Col 1: Brand & Founder */}
                    <div className="flex flex-col gap-6">
                        <div>
                            <h3 className="text-2xl md:text-3xl font-black tracking-wider bg-gradient-to-r from-white via-white to-pink bg-clip-text text-transparent uppercase">
                                {footer[0]?.value || "I-FIT INDONESIA"}
                            </h3>
                            <p className="text-gray-400 text-xs sm:text-sm mt-2 tracking-wide leading-relaxed">
                                Postural Specialist, Scoliosis Correction & Professional Fitness Management.
                            </p>
                        </div>

                        {/* Founder Card (Zero Blur) */}
                        <div className="p-4 rounded-2xl bg-white/[0.04] border border-pink/20">
                            <span className="text-[11px] font-bold text-pink uppercase tracking-widest block">
                                Founded By
                            </span>
                            <h4 className="text-base sm:text-lg font-extrabold text-white mt-1">
                                {footer[1]?.value || "R. RETNA KINANTI DEWI"}
                            </h4>
                            <p className="text-xs sm:text-sm text-biruMuda font-semibold mt-0.5">
                                {footer[2]?.value || "M.Pd.AIFO.FIT - Postural Specialist"}
                            </p>
                        </div>
                    </div>

                    {/* Col 2: Studio Locations */}
                    <div className="flex flex-col gap-4">
                        <h4 className="text-xs font-black text-pink uppercase tracking-widest">
                            Our Locations
                        </h4>

                        <div className="flex flex-col gap-3">
                            {addresses.map((a, index) => (
                                <Link key={index} to="/contact">
                                    <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-pink/[0.08] hover:border-pink/30 hover:translate-x-1 transition-all duration-200 flex items-start gap-3">
                                        <HiLocationMarker className="text-pink text-xl shrink-0 mt-0.5" />
                                        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                                            {a.address}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Col 3: Social Media */}
                    <div className="flex flex-col gap-4">
                        <h4 className="text-xs font-black text-pink uppercase tracking-widest">
                            Connect With Us
                        </h4>

                        <div className="flex flex-col gap-2.5">
                            {socials.map((c, index) => (
                                <a
                                    key={index}
                                    href={c.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                >
                                    <motion.div
                                        whileHover={{ x: 6 }}
                                        transition={{ duration: 0.25, ease: "linear" }}
                                        className="p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-biruMuda/10 hover:border-biruMuda/40 transition-colors duration-200 flex items-center gap-3"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-white p-1 flex items-center justify-center shrink-0">
                                            <img
                                                className="w-full h-full object-contain"
                                                src={process.env.REACT_APP_IMAGE_URL + c.icon}
                                                alt={c.text}
                                            />
                                        </div>
                                        <span className="text-xs sm:text-sm font-semibold text-gray-200">
                                            {c.text}
                                        </span>
                                    </motion.div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="py-6 border-t border-white/5 px-6 sm:px-12">
                <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white p-1 flex items-center justify-center">
                            <img alt="I-FIT Indonesia - Postural Specialist" src="/logo.png" className="w-full h-full object-contain" />
                        </div>
                        <span className="text-xs text-gray-400">
                            &copy; {new Date().getFullYear()} I-FIT Indonesia. All rights reserved.
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={scrollToTop}
                        className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-pink transition-colors cursor-pointer"
                    >
                        <span>Back to Top</span>
                        <span className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs">
                            <HiArrowUp />
                        </span>
                    </button>
                </div>
            </div>
        </footer>
    );
}
